import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../libs/axios';
import Breadcrumb from '../../utils/breadcrumbs';
import { ListLoader } from '../../utils/loader';
import ConfirmationModal from '../../ui/confirmation-modal';

interface AppSetting { key: string; value: string | null; }

const BOOL_FLAGS = [
  { key: 'chat_enabled', label: 'Chat enabled', hint: '1:1 chat in the app' },
  { key: 'chat_groups_enabled', label: 'Chat groups enabled', hint: 'Group chats (usually off for now)' },
  { key: 'maintenance_mode', label: 'Maintenance mode', hint: 'Show maintenance screen in the app' },
];
const TEXT_FLAGS = [
  { key: 'min_app_version_android', label: 'Min Android version', type: 'text' },
  { key: 'min_app_version_ios', label: 'Min iOS version', type: 'text' },
  { key: 'name_change_cap_per_month', label: 'Name changes / month', type: 'number' },
];
// Home "live post" card (embedded YouTube) — admin-driven, shown above the live-matches section.
const LIVE_POST_KEYS = ['live_post_enabled', 'live_post_title', 'live_post_youtube_url', 'live_post_body'];
// Support / Contact Us: admin-editable note at the top of Contact Us (tap-to-call / tap-to-WhatsApp).
const SUPPORT_KEYS = ['support_note_enabled', 'support_note_text', 'support_call_number', 'support_whatsapp_number'];

// DECISION-007: scoring phones and match rules are run from here, not from the server's
// environment file. An empty field means "not set": the server keeps its built-in default
// (or the environment value) and the key is not sent on Save.
interface NumberSetting { key: string; label: string; min: number; max: number; unit: string; fallback: number; hint: string }
const SCORING_NUMBERS: NumberSetting[] = [
  {
    key: 'scoring_takeover_after_seconds', label: 'Take over after', min: 30, max: 3600, unit: 'seconds', fallback: 120,
    hint: 'How long the scoring phone must be silent before another scorer of the match may take over. Who can take over at once is set under "Who can take over at once" below.',
  },
  {
    key: 'scoring_lease_auto_expire_minutes', label: 'Scoring moves by itself after', min: 2, max: 240, unit: 'minutes', fallback: 10,
    hint: 'Silence after which scoring frees itself for phones with no takeover button (store app 1.0.23). Lower it only when most scorers have 1.0.25.',
  },
  {
    key: 'scoring_handover_timeout_seconds', label: 'Unanswered hand-over after', min: 30, max: 600, unit: 'seconds', fallback: 120,
    hint: 'A hand-over request the scoring phone does not answer turns into a takeover offer after this long.',
  },
];
const SCORING_TOGGLES = [
  {
    key: 'scoring_lease_ui_enabled', label: 'Scoring phone screens in the app (1.0.25)', fallback: false,
    hint: '"Scoring: X\'s phone", Ask to score, hand-over and Take over. Turn on after the real-phone test day.',
  },
  {
    key: 'live_fast_path_enabled', label: 'Live fast path (1.0.25)', fallback: true,
    hint: 'Sends each ball straight away. Off sends everything through the ordered upload queue only (rollback lever).',
  },
];
const LEASE_MODES = [
  { value: '', label: 'Server default (environment file)' },
  { value: 'on', label: 'On: one scoring phone per match' },
  { value: 'shadow', label: 'Shadow: record only, do not enforce' },
  { value: 'off', label: 'Off: old scorer lock' },
];
// DECISION-010 §2 ("Let me edit"): the server key `edit_takeover_mode` accepts exactly
// officials / organiser_only / off; empty = the server default, officials.
const TAKEOVER_MODE_KEY = 'edit_takeover_mode';
const TAKEOVER_MODES = [
  { value: '', label: 'Server default (officials)' },
  { value: 'officials', label: 'Officials: organisers, scorers and umpires' },
  { value: 'organiser_only', label: 'Organiser only (the old rule)' },
  { value: 'off', label: 'Off: wait for the scoring phone to go quiet' },
];
// Server keys of kind "mode" (on / shadow / off); empty = the server default, on.
interface ModeSetting { key: string; label: string; hint: string }
const NOTICE_MODES: ModeSetting[] = [
  {
    key: 'scoring_mismatch_notice', label: 'Phone/server mismatch message',
    hint: 'Tells the scoring phone in plain words when it and the server disagree (a ball off, or the innings not over on the server), with a fix (BL-21, match 857). Shadow records only.',
  },
  {
    key: 'viewer_rejoin_mode', label: 'Live score keeps updating after a reconnect',
    hint: 'Puts viewers on app 1.0.23/1.0.25 back on the live score after their connection drops (BL-16). Shadow records only.',
  },
];
const ON_SHADOW_OFF = [
  { value: '', label: 'Server default (on)' },
  { value: 'on', label: 'On' },
  { value: 'shadow', label: 'Shadow: record only' },
  { value: 'off', label: 'Off' },
];
// BL-14 match alerts. Unset = on; the server treats false / 0 / off / no as off.
const PUSH_TOGGLES = [
  {
    key: 'match_start_push_enabled', label: 'Match alerts (toss, start, result)', fallback: true,
    hint: 'Master switch. Off = no toss alert, and start and end alerts exactly as before 26 Oct.',
  },
  {
    key: 'match_push_toss_enabled', label: 'Toss alert', fallback: true,
    hint: 'Off = no alert at the toss.',
  },
  {
    key: 'match_push_result_enabled', label: 'Result alert', fallback: true,
    hint: 'Off = the old end-of-match alert instead of the result alert with the winner.',
  },
];
const RULE_NUMBERS: NumberSetting[] = [
  {
    key: 'hundred_no_ball_runs', label: '100-ball matches: no-ball runs', min: 1, max: 10, unit: 'runs', fallback: 1,
    hint: 'Applies to new 100-ball matches. A value other than 1 needs app 1.0.25 to score correctly.',
  },
  {
    key: 'hundred_wide_runs', label: '100-ball matches: wide runs', min: 1, max: 10, unit: 'runs', fallback: 1,
    hint: 'Applies to new 100-ball matches. A value other than 1 needs app 1.0.25 to score correctly.',
  },
];
// The old-app check is opt-in: empty, unset or "off" = off (the default); a version turns it on.
const MIN_RULES_VERSION_KEY = 'rules_min_app_version';
const isOff = (v: string | undefined | null) => { const s = (v ?? '').trim().toLowerCase(); return s === '' || s === 'off'; };
// How many not-finished matches use non-standard runs, parsed defensively from either a
// `rules_guard_preview` field on GET /app-settings or GET /match-ops/rules-guard-preview.
const guardCountOf = (d: any): number | null => {
  const n = d?.rules_guard_preview?.count ?? d?.count ?? (Array.isArray(d?.matches) ? d.matches.length : undefined)
    ?? (Array.isArray(d?.rules_guard_preview?.matches) ? d.rules_guard_preview.matches.length : undefined);
  return n === undefined || n === null || Number.isNaN(Number(n)) ? null : Number(n);
};
const NEW_KEYS = [
  ...SCORING_NUMBERS.map((s) => s.key),
  ...SCORING_TOGGLES.map((s) => s.key),
  'scoring_lease_mode',
  TAKEOVER_MODE_KEY,
  ...NOTICE_MODES.map((s) => s.key),
  ...PUSH_TOGGLES.map((s) => s.key),
  ...RULE_NUMBERS.map((s) => s.key),
  MIN_RULES_VERSION_KEY,
];
const VERSION_RE = /^\d+(\.\d+){0,3}$/;

const errorMessage = (e: any, fallback: string) => e?.response?.data?.message || fallback;

const Toggle: React.FC<{ on: boolean; onClick: () => void; label?: string }> = ({ on, onClick, label }) => (
  <button type="button" onClick={onClick} aria-label={label} aria-pressed={on}
    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${on ? 'bg-primary-600' : 'bg-gray-300'}`}>
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${on ? 'translate-x-5' : ''}`} />
  </button>
);

const FeatureFlags: React.FC = () => {
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // ADM-2: Save stays off until the live values have loaded once (a failed load used to
  // leave an empty form whose Save wrote blanks over every live flag).
  const [loaded, setLoaded] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Changing the lease mode is an emergency switch: it waits for a confirm.
  const [pendingMode, setPendingMode] = useState<string | null>(null);
  // Changing who can take over editing at once also waits for a confirm.
  const [pendingTakeover, setPendingTakeover] = useState<string | null>(null);
  // The values as loaded: a new key that held a value and is now emptied is sent as ''
  // (back to the server default); one that never held a value is not sent at all.
  const [initial, setInitial] = useState<Record<string, string>>({});
  // Turning the old-app check on waits for a confirm, which shows how many matches it affects.
  const [guardConfirm, setGuardConfirm] = useState(false);
  const [guardCount, setGuardCount] = useState<number | null>(null);
  const [loadedGuardCount, setLoadedGuardCount] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res: any = await api.get('/app-settings');
      const rows: AppSetting[] = Array.isArray(res.data) ? res.data : (res.data?.settings ?? res.data?.rows ?? []);
      setLoadedGuardCount(guardCountOf(res?.rules_guard_preview ? res : res?.data));
      const map: Record<string, string> = {};
      rows.forEach((r) => { map[r.key] = r.value || ''; });
      setValues(map);
      setInitial(map);
      setFieldErrors({});
      setLoaded(true);
    } catch (e) { toast.error('Failed to load flags. Saving is off until they load; reload the page.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setV = (k: string, v: string) => {
    setValues((p) => ({ ...p, [k]: v }));
    setFieldErrors((p) => { if (!p[k]) return p; const n = { ...p }; delete n[k]; return n; });
  };
  const boolOf = (k: string, fallback: boolean) => (values[k] ? values[k] === 'true' : fallback);

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    [...SCORING_NUMBERS, ...RULE_NUMBERS].forEach((s) => {
      const raw = (values[s.key] ?? '').trim();
      if (!raw) return;
      const n = Number(raw);
      if (!Number.isInteger(n) || n < s.min || n > s.max) errs[s.key] = `Whole number from ${s.min} to ${s.max}`;
    });
    const auto = Number((values.scoring_lease_auto_expire_minutes ?? '').trim());
    const takeover = Number((values.scoring_takeover_after_seconds ?? '').trim());
    if (!errs.scoring_lease_auto_expire_minutes && auto && takeover && auto * 60 < takeover) {
      errs.scoring_lease_auto_expire_minutes = 'Must not be shorter than "Take over after"';
    }
    const ver = (values[MIN_RULES_VERSION_KEY] ?? '').trim();
    if (!isOff(ver) && !VERSION_RE.test(ver)) errs[MIN_RULES_VERSION_KEY] = 'A version like 1.0.25, or empty / "off"';
    return errs;
  };

  const save = async () => {
    if (!loaded) { toast.error('The live flags have not loaded, so nothing was saved. Reload the page and try again.'); return; }
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) { toast.error('Fix the highlighted fields first. Nothing was saved.'); return; }
    // Turning the old-app check on (off -> a version) asks first.
    if (isOff(initial[MIN_RULES_VERSION_KEY]) && !isOff(values[MIN_RULES_VERSION_KEY])) {
      let count: number | null = null;
      try {
        const res: any = await api.get('/match-ops/rules-guard-preview');
        count = guardCountOf(res?.data ?? res);
      } catch {
        count = null;
      }
      setGuardCount(count ?? loadedGuardCount);
      setGuardConfirm(true);
      return;
    }
    await doSave();
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const settings: Record<string, string> = {};
      [...BOOL_FLAGS, ...TEXT_FLAGS].forEach((f) => { settings[f.key] = values[f.key] ?? ''; });
      LIVE_POST_KEYS.forEach((k) => { settings[k] = values[k] ?? ''; });
      SUPPORT_KEYS.forEach((k) => { settings[k] = values[k] ?? ''; });
      // New keys go when they hold a value, or as '' when a stored value was cleared
      // (back to the server default). Never-set keys stay unsent.
      NEW_KEYS.forEach((k) => {
        const v = (values[k] ?? '').trim();
        if (v || (initial[k] ?? '') !== '') settings[k] = v;
      });
      await api.put('/app-settings', { settings });
      toast.success('Settings updated');
      load();
    } catch (e: any) { toast.error(errorMessage(e, 'Failed to save'), { duration: 8000 }); }
    finally { setSaving(false); }
  };

  const numberField = (s: NumberSetting) => (
    <div key={s.key}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{s.label}</label>
      <div className="flex items-center gap-2">
        <input type="number" min={s.min} max={s.max} value={values[s.key] || ''} placeholder={String(s.fallback)}
          onChange={(e) => setV(s.key, e.target.value)}
          className={`w-full rounded-lg border px-3 py-2 text-sm ${fieldErrors[s.key] ? 'border-red-400' : 'border-gray-300'}`} />
        <span className="text-xs text-gray-500 whitespace-nowrap">{s.unit}</span>
      </div>
      {fieldErrors[s.key]
        ? <div className="text-xs text-red-600 mt-1">{fieldErrors[s.key]}</div>
        : <div className="text-xs text-gray-400 mt-1">{s.hint} Range {s.min}–{s.max}; empty = {s.fallback}.</div>}
    </div>
  );

  const leaseMode = values.scoring_lease_mode || '';
  const takeoverMode = values[TAKEOVER_MODE_KEY] || '';
  const takeoverLabel = (v: string | null) => TAKEOVER_MODES.find((m) => m.value === v)?.label ?? v ?? '';

  return (
    <div className="p-4 sm:p-6">
      <Breadcrumb />
      <div className="mt-4 mb-6">
        <h1 className="text-xl font-semibold text-gray-900">Feature Flags</h1>
        <p className="text-sm text-gray-500">Toggle app features without a redeploy. Applied on the app's next config fetch.</p>
      </div>
      {loading ? <ListLoader /> : (
        <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 max-w-3xl space-y-5">
          {BOOL_FLAGS.map((f) => (
            <div key={f.key} className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-800">{f.label}</div>
                <div className="text-xs text-gray-400">{f.hint}</div>
              </div>
              <Toggle on={values[f.key] === 'true'} label={f.label} onClick={() => setV(f.key, values[f.key] === 'true' ? 'false' : 'true')} />
            </div>
          ))}
          <div className="border-t border-gray-100 pt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {TEXT_FLAGS.map((f) => (
              <div key={f.key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                <input type={f.type} value={values[f.key] || ''} onChange={(e) => setV(f.key, e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100 pt-5 space-y-4">
            <div>
              <div className="text-sm font-semibold text-gray-900">Scoring phones</div>
              <div className="text-xs text-gray-400">One phone scores each match. These control how quickly scoring can move to another phone. Applied on the server within 30 seconds of saving.</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {SCORING_NUMBERS.map(numberField)}
            </div>
            {SCORING_TOGGLES.map((t) => (
              <div key={t.key} className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-800">{t.label}</div>
                  <div className="text-xs text-gray-400">{t.hint}{values[t.key] ? '' : ` Not set: ${t.fallback ? 'on' : 'off'}.`}</div>
                </div>
                <Toggle on={boolOf(t.key, t.fallback)} label={t.label} onClick={() => setV(t.key, boolOf(t.key, t.fallback) ? 'false' : 'true')} />
              </div>
            ))}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Scoring lease mode</label>
              <select value={leaseMode} onChange={(e) => { if (e.target.value !== leaseMode) setPendingMode(e.target.value); }}
                className="w-full sm:w-96 rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white">
                {LEASE_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <div className="text-xs text-gray-400 mt-1">
                Emergency switch only. &quot;On&quot; stops a second phone from scoring a match (matches 713, 730, 741). Saved with the other settings; logged.
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Who can take over at once (&quot;Let me edit&quot;)</label>
              <select value={takeoverMode} onChange={(e) => { if (e.target.value !== takeoverMode) setPendingTakeover(e.target.value); }}
                className="w-full sm:w-96 rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white">
                {TAKEOVER_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <div className="text-xs text-gray-400 mt-1">
                Who may take editing from the scoring phone straight away (DECISION-010). The previous phone is told who is editing. With &quot;Officials&quot;, umpires may also score. Applied on the server within 30 seconds of Save.
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {NOTICE_MODES.map((s) => (
                <div key={s.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{s.label}</label>
                  <select value={values[s.key] || ''} onChange={(e) => setV(s.key, e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white">
                    {ON_SHADOW_OFF.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <div className="text-xs text-gray-400 mt-1">{s.hint}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-5 space-y-4">
            <div>
              <div className="text-sm font-semibold text-gray-900">Match alerts</div>
              <div className="text-xs text-gray-400">Push alerts to the people of a match (players, officials, organisers, followers), once per person per event. Applied within a minute of saving.</div>
            </div>
            {PUSH_TOGGLES.map((t) => (
              <div key={t.key} className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-800">{t.label}</div>
                  <div className="text-xs text-gray-400">{t.hint}{values[t.key] ? '' : ` Not set: ${t.fallback ? 'on' : 'off'}.`}</div>
                </div>
                <Toggle on={boolOf(t.key, t.fallback)} label={t.label} onClick={() => setV(t.key, boolOf(t.key, t.fallback) ? 'false' : 'true')} />
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100 pt-5 space-y-4">
            <div>
              <div className="text-sm font-semibold text-gray-900">Match formats and rules</div>
              <div className="text-xs text-gray-400">Defaults for new matches. A single match&apos;s rules are changed on its Rules step; a tournament&apos;s on the tournament page.</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {RULE_NUMBERS.map(numberField)}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Oldest app allowed to score matches with non-standard runs</label>
                <input type="text" value={values[MIN_RULES_VERSION_KEY] || ''} placeholder="off"
                  onChange={(e) => setV(MIN_RULES_VERSION_KEY, e.target.value.trim())}
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${fieldErrors[MIN_RULES_VERSION_KEY] ? 'border-red-400' : 'border-gray-300'}`} />
                {fieldErrors[MIN_RULES_VERSION_KEY]
                  ? <div className="text-xs text-red-600 mt-1">{fieldErrors[MIN_RULES_VERSION_KEY]}</div>
                  : <div className="text-xs text-gray-400 mt-1">Off by default. Set to 1.0.25 only after 1.0.25 is in the Play Store and most scorers have it: older phones will then be refused on matches whose no-ball or wide is not 1 run, and told to update.</div>}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-800">Live post (home YouTube card)</div>
                <div className="text-xs text-gray-400">A card above the live-matches section with an embedded YouTube player. Turn on for the launch stream.</div>
              </div>
              <Toggle on={values['live_post_enabled'] === 'true'} label="Live post" onClick={() => setV('live_post_enabled', values['live_post_enabled'] === 'true' ? 'false' : 'true')} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Header</label>
              <input type="text" value={values['live_post_title'] || ''} onChange={(e) => setV('live_post_title', e.target.value)}
                placeholder="CrikStars" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <div className="text-xs text-gray-400 mt-1">Empty &rarr; shows &quot;CrikStars&quot;.</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">YouTube URL</label>
              <input type="text" value={values['live_post_youtube_url'] || ''} onChange={(e) => setV('live_post_youtube_url', e.target.value)}
                placeholder="https://youtube.com/live/... or watch?v=... or youtu.be/..." className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <div className="text-xs text-gray-400 mt-1">Live/video link. Stream must allow embedding. Empty &rarr; card hidden.</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Body</label>
              <textarea value={values['live_post_body'] || ''} onChange={(e) => setV('live_post_body', e.target.value)}
                rows={3} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="border-t border-gray-100 pt-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-800">Support note (Contact Us top banner)</div>
                <div className="text-xs text-gray-400">Shown at the top of the app's Contact Us screen with tap-to-call / tap-to-WhatsApp. Turn off to hide.</div>
              </div>
              <Toggle on={values['support_note_enabled'] === 'true'} label="Support note" onClick={() => setV('support_note_enabled', values['support_note_enabled'] === 'true' ? 'false' : 'true')} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Note text</label>
              <textarea value={values['support_note_text'] || ''} onChange={(e) => setV('support_note_text', e.target.value)}
                rows={2} placeholder="For urgent problems, please call us or message on WhatsApp." className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Call number</label>
                <input type="text" value={values['support_call_number'] || ''} onChange={(e) => setV('support_call_number', e.target.value)}
                  placeholder="+91XXXXXXXXXX" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">WhatsApp number</label>
                <input type="text" value={values['support_whatsapp_number'] || ''} onChange={(e) => setV('support_whatsapp_number', e.target.value)}
                  placeholder="+91XXXXXXXXXX" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button onClick={save} disabled={saving || !loaded} className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-60">
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={pendingMode !== null}
        onClose={() => setPendingMode(null)}
        onConfirm={() => { if (pendingMode !== null) setV('scoring_lease_mode', pendingMode); setPendingMode(null); }}
        title={`Change the scoring lease mode to "${LEASE_MODES.find((m) => m.value === pendingMode)?.label ?? pendingMode}"?`}
        description={pendingMode === 'on'
          ? 'Only one phone may score each match. Takes effect within 30 seconds of Save.'
          : 'This switches off the one-phone protection: a second phone can score a match again (the cause of matches 713, 730 and 741). Use only in an emergency. Takes effect within 30 seconds of Save.'}
      />
      <ConfirmationModal
        isOpen={pendingTakeover !== null}
        onClose={() => setPendingTakeover(null)}
        onConfirm={() => { if (pendingTakeover !== null) setV(TAKEOVER_MODE_KEY, pendingTakeover); setPendingTakeover(null); }}
        title={`Change who can take over at once to "${takeoverLabel(pendingTakeover)}"?`}
        description={pendingTakeover === 'off'
          ? 'Nobody can take over straight away; another phone waits until the scoring phone has been quiet for "Take over after". Takes effect within 30 seconds of Save.'
          : pendingTakeover === 'organiser_only'
            ? 'Only the match or tournament organiser can take over straight away; scorers and umpires wait. Takes effect within 30 seconds of Save.'
            : 'Any match official (organiser, co-organiser, scorer, umpire) can take over editing straight away with "Let me edit", and umpires may score. Takes effect within 30 seconds of Save.'}
      />
      <ConfirmationModal
        isOpen={guardConfirm}
        onClose={() => setGuardConfirm(false)}
        onConfirm={async () => { setGuardConfirm(false); await doSave(); }}
        loading={saving}
        title={`Turn on the old-app check (app ${(values[MIN_RULES_VERSION_KEY] ?? '').trim()} or later)?`}
        description={`${guardCount != null ? `${guardCount} match${guardCount === 1 ? '' : 'es'} not yet finished use${guardCount === 1 ? 's' : ''} non-standard runs. ` : ''}`
          + 'Phones older than this version will be refused on matches whose no-ball or wide is not 1 run, and told to update. Saves all the settings on this page.'}
      />
    </div>
  );
};
export default FeatureFlags;
